import React from 'react';
import { ArrowRight, Target, TrendingUp, Lock, CheckCircle, Sparkles, ChevronDown, ChevronUp } from 'lucide-react';
import { useHubState } from '../../../../contexts/HubStateContext';
import { getStudentTier } from '../utils/tierFramework';

const AdaptiveHeader = ({
  currentView,
  portfolioStatus = {},
  studentProfile = {},
  onNavigate,
  colleges = []
}) => {
  const {
    shouldShowPhaseUnlockCelebration,
    isFirstTimeInView,
    recordViewVisit
  } = useHubState();

  const {
    view1_unlocked = false,
    view2_unlocked = false,
    view3_unlocked = false,
    tiers_locked = false
  } = portfolioStatus;

  // Record view visit on mount/view change
  React.useEffect(() => {
    if (currentView) {
      recordViewVisit(currentView);
    }
  }, [currentView, recordViewVisit]);

  // Helper: Get student tier classification for tier view using tierFramework
  const getStudentTierData = () => {
    if (!studentProfile) return null;

    // Use the comprehensive tier framework
    const studentTier = getStudentTier(studentProfile);
    
    // Extract test scores for display (using the same comprehensive field checking as tierFramework)
    const sat = studentProfile.sat_total ||
                studentProfile.sat_score ||
                studentProfile.test_scores?.sat ||
                studentProfile.test_scores?.sat_total ||
                0;

    const act = studentProfile.act_composite ||
                studentProfile.act_score ||
                studentProfile.test_scores?.act ||
                studentProfile.test_scores?.act_composite ||
                0;

    const gpa = studentProfile.gpa_weighted ||
                studentProfile.gpa_unweighted ||
                studentProfile.gpa ||
                studentProfile.academic_performance?.gpa ||
                studentProfile.academic_performance?.gpa_weighted ||
                studentProfile.academic_performance?.gpa_unweighted ||
                0;

    return {
      label: studentTier.label,
      id: studentTier.id,
      sat,
      gpa,
      act,
      description: studentTier.description
    };
  };

  // Tier Analysis unlock celebration
  if (currentView === 'tier' && shouldShowPhaseUnlockCelebration('phase2') && view2_unlocked) {
    return (
      <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 rounded-xl mb-6 overflow-hidden border-2 border-indigo-800 shadow-lg">
        <div className="p-6 flex items-start gap-4">
          <div className="bg-white rounded-full p-3 shadow-md">
            <Sparkles className="w-6 h-6 text-indigo-600" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="text-xl font-bold text-white">Tier Analysis Unlocked!</h3>
              <span className="px-2 py-1 bg-white text-indigo-700 text-xs font-bold rounded-full uppercase">
                New
              </span>
            </div>
            <p className="text-indigo-100 text-sm mb-3">
              Great progress! You've completed enough colleges to access <strong>Tier Analysis</strong>.
            </p>

            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 mb-4">
              <p className="text-white text-sm font-medium mb-2">Now you can:</p>
              <ul className="text-indigo-100 text-sm space-y-1.5">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>Evaluate balance within reach, target, and safety tiers</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>Get AI-powered confidence scores for each tier</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>Discover alternative school recommendations</span>
                </li>
              </ul>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => onNavigate?.('strategic')}
                className="px-4 py-2 bg-white text-indigo-700 rounded-lg font-medium text-sm hover:bg-indigo-50 transition-colors"
              >
                Continue Building Portfolio
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Portfolio Strategy unlock celebration
  if (currentView === 'portfolio' && shouldShowPhaseUnlockCelebration('phase3') && view3_unlocked) {
    return (
      <div className="bg-gradient-to-r from-violet-600 to-violet-700 rounded-xl mb-6 overflow-hidden border-2 border-violet-800 shadow-lg">
        <div className="p-6 flex items-start gap-4">
          <div className="bg-white rounded-full p-3 shadow-md">
            <Target className="w-6 h-6 text-violet-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-bold text-white mb-2">
              Portfolio Strategy Unlocked - Portfolio Complete!
            </h3>
            <p className="text-violet-100 text-sm mb-3">
              Your 4-4-4 portfolio is locked and ready for strategic execution.
            </p>

            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 mb-4">
              <p className="text-white text-sm font-medium mb-2">Access Portfolio Strategy for:</p>
              <ul className="text-violet-100 text-sm space-y-1.5">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>ED strategy recommendation with confidence scoring</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>Essay overlap analysis and writing order guidance</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>Application timeline and strategic recommendations</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Strategic Overview - contextual guidance
  if (currentView === 'strategic') {
    const isFirstTime = isFirstTimeInView('strategic');
    const hasMinColleges = colleges.length >= 3;

    if (isFirstTime || !view2_unlocked) {
      return (
        <div className="bg-gradient-to-br from-teal-50 to-teal-100 border-2 border-teal-300 rounded-xl p-6 mb-6">
          <div className="flex items-start gap-4">
            <div className="bg-teal-600 rounded-lg p-3 shadow-md">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-slate-900 mb-2">
                Building Your Strategic Portfolio
              </h3>

              <p className="text-sm text-slate-700 leading-relaxed mb-3">
                You're in <span className="font-semibold text-teal-800">Phase 1: Exploration & Selection</span>.
                Use the comparison tools below to evaluate colleges and build your 4-4-4 portfolio.
              </p>

              <div className="bg-white border-l-4 border-teal-500 rounded-r-lg pl-4 py-3 pr-3">
                <p className="text-sm text-slate-800">
                  <span className="font-semibold">To unlock Tier Analysis:</span> Complete one tier (4 colleges in reach, target, or safety)
                </p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Returning user - compact status
    return null;
  }

  // Tier Analysis - show student tier badge + contextual guidance
  if (currentView === 'tier') {
    const studentTier = getStudentTierData();

    return (
      <div className="space-y-4 mb-6">
        {/* Student Tier Classification */}
        {studentTier && (
          <div className="bg-slate-50 rounded-lg border-2 border-slate-800 p-4 shadow-md">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div>
                  <h3 className="text-sm font-bold text-slate-800 mb-1">
                    Your Profile: {studentTier.label} (Tier {studentTier.id})
                  </h3>
                  <div className="flex items-center gap-4 text-xs text-slate-900">
                    {studentTier.sat > 0 && (
                      <span>SAT: <span className="font-regular text-xs text-slate-800">{studentTier.sat}</span></span>
                    )}
                    {studentTier.gpa > 0 && (
                      <span>GPA: <span className="font-regular text-xs text-slate-800">{studentTier.gpa.toFixed(2)}</span></span>
                    )}
                    {studentTier.act > 0 && (
                      <span>ACT: <span className="font-regular text-xs text-slate-800">{studentTier.act}</span></span>
                    )}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-slate-800 mb-1">{studentTier.description}</p>
                <p className="text-xs text-slate-800 font-medium">Tier definitions personalized to your stats</p>
              </div>
            </div>
          </div>
        )}

        {/* Contextual guidance for tier view */}
        {isFirstTimeInView('tier') && (
          <div className="bg-indigo-50 border-2 border-indigo-200 rounded-lg p-5">
            <div className="flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="text-sm font-bold text-slate-900 mb-2">Welcome to Tier Analysis</h4>
                <p className="text-sm text-slate-700 leading-relaxed">
                  Click on each tier tab below to analyze your reach, target, and safety schools.
                  You'll get AI-powered insights on balance, confidence scores, and alternative recommendations.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Portfolio view - compact status
  if (currentView === 'portfolio') {
    if (tiers_locked) {
      return (
        <div className="bg-gradient-to-br from-violet-50 to-violet-100 border-2 border-violet-300 rounded-lg p-5 mb-6">
          <div className="flex items-center gap-3">
            <div className="bg-violet-600 rounded-lg p-2.5">
              <CheckCircle className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-slate-900">Portfolio Locked & Ready</h3>
              <p className="text-xs text-slate-600 mt-1">
                Analyzing 12 schools across all tiers for strategic execution planning
              </p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-5 mb-6">
        <div className="flex items-start gap-3">
          <Lock className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="text-sm font-bold text-slate-900 mb-1">Complete Your Portfolio to Unlock</h4>
            <p className="text-sm text-slate-700">
              You need to complete and lock all 3 tiers (12 colleges total) to access Portfolio Strategy features.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default AdaptiveHeader;
